import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { Header } from "@/components/header";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useServerFn } from "@tanstack/react-start";
import { getQuizAnalytics } from "@/lib/quiz.functions";
import { useErrorReporter } from "@/components/error-reporter";
import {
  AnswerSplitDonut,
  QuestionAccuracyChart,
  SessionsTrendCard,
} from "@/components/analytics/charts";
import {
  ArrowLeft,
  BarChart3,
  Users,
  Trophy,
  Target,
  Activity,
  CheckCircle2,
  XCircle,
  Clock,
  TrendingUp,
  Calendar,
} from "lucide-react";

export const Route = createFileRoute("/_authenticated/quizzes/$id/analytics")({
  component: QuizAnalyticsPage,
});

function QuizAnalyticsPage() {
  const { id } = Route.useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const fetchAnalytics = useServerFn(getQuizAnalytics);
  const { reportError } = useErrorReporter();

  useEffect(() => {
    async function load() {
      try {
        const result = await fetchAnalytics({ data: { quizId: id } });
        setData(result);
      } catch (e) {
        reportError(e, { area: "quiz.analytics", retry: () => load() });
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [id]);

  const totalScorePossible = useMemo(() => {
    if (!data) return 0;
    return data.questions.reduce((sum, q) => sum + (q.points ?? 0), 0);
  }, [data]);

  const answerSplit = useMemo(() => {
    if (!data) return { correct: 0, incorrect: 0 };
    return data.questionAnalytics.reduce(
      (acc, q) => ({
        correct: acc.correct + (q.correctCount ?? 0),
        incorrect: acc.incorrect + (q.incorrectCount ?? 0),
      }),
      { correct: 0, incorrect: 0 },
    );
  }, [data]);

  if (loading) {
    return (
      <div className="min-h-screen bg-bg">
        <Header />
        <div className="container mx-auto px-4 py-10 max-w-6xl">
          <p className="text-muted">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-bg">
        <Header />
        <div className="container mx-auto px-4 py-10 max-w-6xl">
          <Card className="p-6 text-center">
            <BarChart3 className="size-12 mx-auto text-muted" />
            <h2 className="text-xl font-bold mt-4">Analytics unavailable</h2>
            <p className="text-muted mt-1">Could not load analytics for this quiz.</p>
            <Button asChild variant="outline" className="mt-4">
              <Link to="/dashboard">
                <ArrowLeft className="size-4" /> Back to dashboard
              </Link>
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg">
      <Header />
      <div className="container mx-auto px-4 py-10 max-w-6xl space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <Button asChild variant="ghost" size="icon" className="mt-1">
              <Link to="/dashboard">
                <ArrowLeft className="size-4" />
              </Link>
            </Button>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-3xl font-bold tracking-tight">{data.quiz.title}</h1>
                <Badge variant="outline" className="flex items-center gap-1">
                  <BarChart3 className="size-3" /> Analytics
                </Badge>
              </div>
              {data.quiz.description && <p className="text-muted mt-1">{data.quiz.description}</p>}
              <p className="text-xs text-muted mt-1">
                {data.overall.questionsCount} questions · {totalScorePossible} total points
              </p>
            </div>
          </div>
          <Button asChild variant="outline">
            <Link to="/dashboard">Back to dashboard</Link>
          </Button>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={Activity} label="Sessions played" value={data.overall.totalSessions} />
          <StatCard icon={Users} label="Total players" value={data.overall.totalParticipants} />
          <StatCard
            icon={Target}
            label="Avg score"
            value={`${data.overall.avgScore}${totalScorePossible > 0 ? `/${totalScorePossible}` : " pts"}`}
          />
          <StatCard
            icon={Trophy}
            label="Highest score"
            value={`${data.overall.highestScore}${totalScorePossible > 0 ? `/${totalScorePossible}` : " pts"}`}
            accent
          />
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <StatCard icon={TrendingUp} label="Total responses" value={data.overall.totalResponses} />
          <StatCard
            icon={Target}
            label="Lowest score"
            value={`${data.overall.lowestScore}${totalScorePossible > 0 ? `/${totalScorePossible}` : " pts"}`}
          />
          <StatCard icon={BarChart3} label="Questions" value={data.overall.questionsCount} />
        </div>

        <div className="grid lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2">
            <QuestionAccuracyChart questions={data.questionAnalytics} />
          </div>
          <AnswerSplitDonut correct={answerSplit.correct} incorrect={answerSplit.incorrect} />
        </div>

        <SessionsTrendCard sessions={data.sessions} />

        {data.questionAnalytics.length > 0 && (
          <Card className="p-6">
            <h2 className="font-bold text-lg flex items-center gap-2 mb-4">
              <BarChart3 className="size-5 text-accent" /> Question performance
            </h2>
            <div className="space-y-3">
              {data.questionAnalytics.map((q, idx) => (
                <div
                  key={q.id}
                  className="border rounded-lg p-4 hover:bg-surface-2/30 transition-colors"
                >
                  <div className="flex justify-between items-start gap-4 mb-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-semibold text-muted">Q{idx + 1}</span>
                        <Badge variant="secondary" className="text-xs">
                          {q.type.replace("_", " ")}
                        </Badge>
                        <Badge
                          variant={
                            q.accuracy >= 70
                              ? "default"
                              : q.accuracy >= 40
                                ? "secondary"
                                : "destructive"
                          }
                          className="text-xs"
                        >
                          {q.accuracy}% correct
                        </Badge>
                      </div>
                      <p className="font-medium leading-relaxed">{q.question_text}</p>
                      {q.correct_answer && (
                        <p className="text-xs text-muted mt-1">
                          Correct answer:{" "}
                          <span className="text-success font-medium">{q.correct_answer}</span>
                        </p>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-bold text-xl text-accent">{q.accuracy}%</div>
                      <div className="text-xs text-muted">{q.points} pts</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
                    <div className="flex items-center gap-2 text-sm">
                      <Users className="size-4 text-muted" />
                      <span className="text-muted">Responses:</span>
                      <span className="font-medium">{q.totalResponses}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className="size-4 text-success" />
                      <span className="text-muted">Correct:</span>
                      <span className="font-medium text-success">{q.correctCount}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <XCircle className="size-4 text-destructive" />
                      <span className="text-muted">Wrong:</span>
                      <span className="font-medium text-destructive">{q.incorrectCount}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Clock className="size-4 text-muted" />
                      <span className="text-muted">Avg time:</span>
                      <span className="font-medium">{(q.avgResponseTime / 1000).toFixed(1)}s</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {data.sessions.length > 0 && (
          <Card className="p-6">
            <h2 className="font-bold text-lg flex items-center gap-2 mb-4">
              <Calendar className="size-5 text-accent" /> Session history
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-3 text-sm font-medium text-muted">Session</th>
                    <th className="text-left py-2 px-3 text-sm font-medium text-muted">Status</th>
                    <th className="text-left py-2 px-3 text-sm font-medium text-muted">PIN</th>
                    <th className="text-left py-2 px-3 text-sm font-medium text-muted">Players</th>
                    <th className="text-left py-2 px-3 text-sm font-medium text-muted">
                      Responses
                    </th>
                    <th className="text-left py-2 px-3 text-sm font-medium text-muted">
                      Avg/High/Low
                    </th>
                    <th className="text-left py-2 px-3 text-sm font-medium text-muted">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {data.sessions.map((s) => (
                    <tr key={s.id} className="border-b hover:bg-surface-2/30 transition-colors">
                      <td className="py-3 px-3 text-sm font-mono text-muted">
                        {s.id.slice(0, 8)}...
                      </td>
                      <td className="py-3 px-3">
                        <Badge
                          variant={
                            s.status === "ended"
                              ? "default"
                              : s.status === "lobby"
                                ? "secondary"
                                : "outline"
                          }
                          className="text-xs"
                        >
                          {s.status}
                        </Badge>
                      </td>
                      <td className="py-3 px-3 text-sm font-bold">{s.pin_code}</td>
                      <td className="py-3 px-3 text-sm">{s.participantCount}</td>
                      <td className="py-3 px-3 text-sm">{s.responseCount}</td>
                      <td className="py-3 px-3 text-sm">
                        {s.participantCount > 0
                          ? `${s.avgScore}/${s.highestScore}/${s.lowestScore}`
                          : "-"}
                      </td>
                      <td className="py-3 px-3 text-sm text-muted">
                        {new Date(s.created_at).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {data.sessions.length === 0 && (
          <Card className="p-6 text-center">
            <Activity className="size-12 mx-auto text-muted" />
            <h2 className="text-xl font-bold mt-4">No sessions yet</h2>
            <p className="text-muted mt-1">Host this quiz to start collecting analytics data.</p>
            <Button asChild className="mt-4 bg-gradient-primary">
              <Link to="/dashboard">Go to dashboard</Link>
            </Button>
          </Card>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <Card
      className={`p-5 ${accent ? "bg-gradient-primary text-accent-foreground border-0" : "bg-gradient-card border-2"}`}
    >
      <Icon className="size-5 opacity-80" />
      <p className="text-xs opacity-80 mt-2">{label}</p>
      <p className="text-2xl font-bold font-display">{value}</p>
    </Card>
  );
}
